import { Box, Flex, HStack, VStack, Text, IconButton, Tag } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import { FiPlay, FiSettings, FiTrash2 } from 'react-icons/fi';

const StartNode = ({ data }) => {
  return (
    <Box
      bg="white"
      borderWidth="2px"
      borderColor={data.selected ? 'green.600' : '#00c951'}
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="240px"
    >
      {/* Header */}
      <Flex
        bg="#00c951"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        justify="space-between"
      >
        <HStack spacing={2}>
          <FiPlay />
          <Text noOfLines={1} fontSize="sm" fontWeight="medium">
            {data.label || 'Flow Start'}
          </Text>
        </HStack>
        <HStack>
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            icon={<FiSettings />}
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit?.(data.id);
            }}
          />
          
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            icon={<FiTrash2 />}
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(data.id);
            }}
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3.5}>
        {/* Trigger Type Label */}
        <Text fontSize="sm" fontWeight="medium" mb={1.5}>
          {data.triggerType === 'api'
            ? 'API Based Entry'
            : data.triggerType === 'event'
            ? 'Event Trigger'
            : data.triggerType === 'customer_segment'
            ? 'Customer Segment'
            : 'Manual Start'}
        </Text>

        {/* API Trigger Details */}
        {data.triggerType === 'api' && (
          <VStack align="start" spacing={2} mt={2}>
            <Box 
              bg="blue.50" 
              borderColor="blue.200" 
              borderWidth="1px"
              rounded="md" 
              px={2} 
              py={1.5} 
              w="full"
            >
              <Text fontSize="xs" color="blue.900" fontWeight="500" mb={1}>
                Webhook Endpoint
              </Text>
              <Text 
                fontSize="10px" 
                fontFamily="mono" 
                color="blue.700"
                wordBreak="break-all"
              >
                /v1/flows/{'{'}1{'}'}
              </Text>
            </Box>
            
            {data.apiAuthMethod && (
              <HStack spacing={2} w="full">
                <Text fontSize="xs" color="gray.600">Auth:</Text>
                <Tag colorScheme="purple" fontSize="10px">
                  {data.apiAuthMethod === 'bearer' ? 'Bearer Token' :
                   data.apiAuthMethod === 'apikey' ? 'API Key' :
                   'Basic Auth'}
                </Tag>
              </HStack>
            )}
          </VStack>
        )}

        {/* Event Trigger Details */}
        {data.triggerType === 'event' && data.eventName && (
          <VStack align="start" spacing={1} fontSize="xs" color="gray.600" mt={1.5}>
            <HStack spacing={1.5}>
              <Text fontWeight="500">{data.eventName}</Text>
            </HStack>
            {data.eventTriggerMode === 'condition' && data.eventProperties?.length > 0 && (
              <Box bg="green.50" rounded="md" px={2} py={1} mt={1} fontSize="xs" w="full">
                <Text fontWeight="500" color="green.800">
                  {data.eventProperties.length} condition{data.eventProperties.length !== 1 ? 's' : ''}
                </Text>
              </Box>
            )}
          </VStack>
        )}

        {/* Customer Segment Details */}
        {data.triggerType === 'customer_segment' && (
          <VStack spacing={1.5} align="start" mt={2}>
            <Text fontSize="sm" fontWeight="500">
              {data.segmentName || 'Selected Segment'}
            </Text>

            <Box 
              fontSize="xs" 
              px={2} 
              py={1} 
              bg="#f0fdf4" 
              color="#64748b"
              w="full"
              rounded="md"
              cursor="pointer"
              _hover={{ textDecoration: 'underline' }}
            >
              {data.customerCount?.toLocaleString() || 0} users
            </Box>

            {/* Segment Entry Mode Badge */}
            {data.segmentEntryMode === "new" && (
              <Box 
                fontSize="xs" 
                px={2} 
                py={1} 
                bg="var(--brand-tint)" 
                color="var(--brand-primary-hover)"
                w="full"
                rounded="md"
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
              >
                New Customers Only
              </Box>
            )}

            {data.segmentEntryMode === "api" && (
              <Box 
                fontSize="xs" 
                px={2} 
                py={1} 
                bg="#fff7ed" 
                color="#ea580c"
                w="full"
                rounded="md"
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
              >
                API Based Entry
              </Box>
            )}

            {/* Schedule Info */}
            {data.enableSchedule && data.segmentEntryMode === 'existing' && (
              <Box 
                fontSize="xs" 
                px={2} 
                py={1} 
                bg="#faf5ff" 
                color="#7c3aed"
                w="full"
                rounded="md"
              >
                <Text fontWeight="500" mb={0.5}>
                  Scheduled: {data.scheduleType === 'daily' ? 'Daily' :
                             data.scheduleType === 'weekly' ? 'Weekly' :
                             'Monthly'}
                </Text>
                <Text fontSize="10px">
                  at {data.scheduleTime} {data.scheduleTimezone}
                </Text>
              </Box>
            )}
          </VStack>
        )}

        {/* Manual Start */}
        {data.triggerType === 'manual' && (
          <Text fontSize="xs" color="gray.600" mt={1.5}>
            Campaign starts manually
          </Text>
        )}
      </Box>

      <Handle type="source" position={Position.Right} style={{ background: 'black' }} />
    </Box>
  );
};

export default StartNode;